import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

/**
 * LockedAction
 *
 * Wrap any free-user-restricted CTA so the button stays visually present
 * but tappable only to surface the upgrade prompt. This is the "see what
 * you're missing exactly when you reach for it" pattern.
 *
 * Usage:
 *   <LockedAction
 *     locked={user?.tier === "free"}
 *     reason="bundle"
 *     tier="vault"
 *     label="Export PDF Evidence Bundle"
 *   >
 *     <button className="btn-primary">Export PDF Evidence Bundle</button>
 *   </LockedAction>
 *
 * When locked: replaces the children with a faded version that shows a
 * small lock icon. Click opens a one-screen explainer card and a link to
 * /pricing?upgrade=<reason>. The explainer is a tiny inline popover so
 * we never block the user with a modal.
 *
 * When not locked: renders children unchanged.
 */
export default function LockedAction({
  locked,
  reason = "this feature",
  tier = "vault",
  label,
  helper,
  children,
  className = "",
  inline = false,
  testid,
}) {
  const [open, setOpen] = useState(false);
  if (!locked) return children;

  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  const unlock = REASON_COPY[reason] || REASON_COPY.default;

  return (
    <span className={`relative inline-block ${inline ? "" : "w-full"} ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative inline-flex items-center gap-1.5 opacity-60 hover:opacity-80 transition-opacity cursor-pointer ${inline ? "" : "w-full"}`}
        data-testid={testid || `locked-${reason}`}
        aria-label={`${label || "Action"} (upgrade to unlock)`}
      >
        <span className={inline ? "" : "w-full pointer-events-none"}>{children}</span>
        <Lock
          size={12}
          strokeWidth={2}
          className="absolute top-1.5 right-1.5 text-[#8A6D3B] bg-[#FAF6E8] rounded-full p-0.5 w-4 h-4 border border-[#8A6D3B]/30"
          aria-hidden="true"
        />
      </button>
      {open && (
        <span
          role="dialog"
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#8A6D3B]/40 shadow-lg p-3 text-left"
          style={{ minWidth: "260px" }}
          data-testid={`locked-popover-${reason}`}
        >
          <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#8A6D3B] mb-1.5 flex items-center gap-1">
            <Lock size={10} strokeWidth={2} /> {tierLabel} feature
          </span>
          <span className="block font-serif text-[14px] text-[#0D2B52] leading-snug mb-1.5">
            {unlock.title}
          </span>
          <span className="block text-[12px] text-[#6B7280] leading-snug mb-2">
            {helper || unlock.body}
          </span>
          <Link
            to={`/pricing?upgrade=${reason}`}
            className="inline-block text-[12px] font-mono uppercase tracking-[0.12em] text-white bg-[#1B6C62] px-3 py-1.5 hover:opacity-90"
            data-testid={`locked-cta-${reason}`}
            onClick={() => setOpen(false)}
          >
            See upgrade options →
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="block mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] hover:text-[#0D2B52]"
          >
            Maybe later
          </button>
        </span>
      )}
    </span>
  );
}

const REASON_COPY = {
  default: {
    title: "This is part of a paid plan.",
    body: "You can still record everything, just not act on it from the free plan.",
  },
  download: {
    title: "Downloading is on the paid plans.",
    body: "You can view, share inside the app, plus keep adding evidence on free. Downloading and sending out files needs Vault or higher.",
  },
  bundle: {
    title: "Evidence bundles are on the paid plans.",
    body: "The PDF bundle pulls every document, photo, plus timestamp into one tribunal-ready file. Available on Vault and up.",
  },
  letter_send: {
    title: "Sending letters needs Shield.",
    body: "You can draft your one free letter and refine it as much as you like. Sending it via email tracks the reply and sits behind Shield.",
  },
  letter_full: {
    title: "Unlock the full letter.",
    body: "Compass shows you the first portion so you can see the tone. The legal citations, your specific asks plus the closing arrive when you upgrade.",
  },
  letter_more: {
    title: "You've used your one free draft.",
    body: "Upgrade to Shield to draft and send unlimited letters, or activate Dispute & Tribunal Mode if you're heading to a hearing.",
  },
  letter_tone_nuclear: {
    title: "The Nuclear tone is a Shield feature.",
    body: "Point-by-point denial, counter-allegations backed by your evidence plus formal notice of tribunal readiness. Free drafts stay friendly, professional or firm.",
  },
  letter_deep_scan: {
    title: "Deep Evidence Scan needs Shield.",
    body: "Compass reads your entire case file first (documents, Deep Reads, timeline, move-out flags), then drafts a letter grounded in your strongest evidence.",
  },
  transcribe: {
    title: "You've used your free transcriptions.",
    body: "Recording stays free forever. Whisper transcription is the AI cost, so we cap it at 2 lifetime on free. Shield or Dispute Mode unlocks it daily.",
  },
  photo_trail: {
    title: "Photo Trail check-ins need Vault.",
    body: "Free users get the first photo trail. Recurring monthly check-ins (the tribunal-winning ones) are on Vault and up.",
  },
  deep_read: {
    title: "Your monthly Deep Reads are used.",
    body: "Shield and Pro re-up your Deep Read quota monthly. Vault gives you 15 per month.",
  },
};
